import axios from '../../Axios/axiosConfig'
import Cookies from 'js-cookie';

import {CURRENT_USER_ID_COOKIE, UNKNOWN_USER_ID, VIEW_AS_USER_ID_COOKIE} from "../../Constants/constants";

export class UsersService {
    STATUS_CODE_OK = 200;
    STATUS_CODE_CREATED = 201;

    baseUrl;

    roles;
    confirmationStatuses;

    constructor(serverUrl) {
        this.baseUrl = serverUrl;

        this.roles = new Map();
        this.roles.set('admin', 'Administrator');
        this.roles.set('tester', 'Tester');
        this.roles.set('technician', 'Technician');
        this.roles.set('user', 'User');
        this.roles.set('site_manager', 'Site Manager');
        this.roles.set('organization_manager', 'Organization Manager');
        this.roles.set('operator', 'Operator');
        this.roles.set('ict', 'ICT');
        this.roles.set('distributor', 'Distributor');

        this.confirmationStatuses = new Map();
        this.confirmationStatuses.set('UNCONFIRMED', ['Unconfirmed', 'User has been created but not confirmed.']);
        this.confirmationStatuses.set('CONFIRMED', ['Confirmed', 'User has been confirmed.']);
        this.confirmationStatuses.set('ARCHIVED', ['Archived', 'User is no longer active.']);
        this.confirmationStatuses.set('COMPROMISED', ['Compromised', 'User is disabled due to a potential security threat.']);
        this.confirmationStatuses.set('UNKNOWN', ['Unknown', 'User status is unknown.']);
        this.confirmationStatuses.set('RESET_REQUIRED', ['Reset required', 'User is confirmed, but the user must request a code and reset their password before they can sign in.']);
        this.confirmationStatuses.set('FORCE_CHANGE_PASSWORD', ['Force change password', 'The user is confirmed and the user can sign in using a temporary password, but on first sign-in, the user must change their password to a new value before doing anything else.']);
    }

    getAllUsers() {
        return axios.get(this.baseUrl)
            .then(response => {
                let viewAsUserId = Cookies.get(VIEW_AS_USER_ID_COOKIE);
                if (!viewAsUserId) {
                    viewAsUserId = Cookies.get(CURRENT_USER_ID_COOKIE);
                    Cookies.set(VIEW_AS_USER_ID_COOKIE, viewAsUserId);
                }

                const users = [];
                response.data.forEach(userInfo => {
                    const userData = this.constructUserData(userInfo, viewAsUserId);
                    users.push(userData);
                });
                users.sort((a, b) => a.userId - b.userId);

                return users;
            }).catch(error => {
                console.error(error);
                return [];
            });
    }

    findUser(userId) {
        return axios.get(`${this.baseUrl}/${userId}`)
            .then(response => {
                return this.constructUserData(response.data, UNKNOWN_USER_ID);
            }).catch(error => {
                return this.constructUserData(null, UNKNOWN_USER_ID);
            });
    }

    constructUserData(userInfo, viewAsUserId) {
        if (!userInfo)
            return null;

        const fullName = `${userInfo.firstName} ${userInfo.lastName}`;

        const confirmationStatus = userInfo.confirmationStatus && this.confirmationStatuses.has(userInfo.confirmationStatus)
            ? this.confirmationStatuses.get(userInfo.confirmationStatus)
            : this.confirmationStatuses.get('UNKNOWN');

        const confirmationStatusText = confirmationStatus[0];
        const confirmationStatusTooltip = confirmationStatus[1];

        const role = userInfo.role && this.roles.has(userInfo.role) ? this.roles.get(userInfo.role) : 'Unknown';

        return {
            userKey: userInfo.username,
            userId: userInfo.id,
            fullName: fullName,
            email: userInfo.email,
            accountStatus: userInfo.accountStatus,
            confirmationStatusText: confirmationStatusText,
            confirmationStatusTooltip: confirmationStatusTooltip,
            role: role,
            creationTime: userInfo.createdAt,
            viewAsUserId: userInfo.id === Number(viewAsUserId)
        };
    }

    async addNewUser(userId) {
        const response = await axios.post(`${this.baseUrl}/${userId}`);
        return response.status === this.STATUS_CODE_CREATED;
    }

    async resetPassword(userKey) {
        const response = await axios.post(`${this.baseUrl}/${userKey}/reset-password`);
        return response.status === this.STATUS_CODE_OK;
    }

    async removeUser(userKey) {
        const response = await axios.delete(`${this.baseUrl}/${userKey}`);
        return response.status === this.STATUS_CODE_OK;
    }

    getViewAsUser() {
        return Cookies.get(VIEW_AS_USER_ID_COOKIE);
    }

    setViewAsUser(userId) {
        Cookies.set(VIEW_AS_USER_ID_COOKIE, userId, {expires: 1 /* day */});
    }
}